import React, { ReactElement } from 'react'

import { logProductViewContentTikTok } from '@mindfulchefuk/utils/analytics'
import { openModal } from '@mindfulchefuk/utils/modalUtils'
import { OnboardingRecipeSnippet } from '@mindfulchefuk/features/Onboarding/components/OnboardingRecipes/OnboardingRecipeSnippet'
import { Box, Flex, Grid } from '@mindfulchefuk/design-system'
import Loader from '@mindfulchefuk/components/Loader'
import { ProductType } from '@mindfulchefuk/analytics'
import { useCMSPageRecipeOnboarding } from '@mindfulchefuk/features/CMS/hooks/useCMSPageRecipeOnboarding'
import { RecipeCardWithClaims } from '@mindfulchefuk/features/Recipes/components/RecipeCardWithClaims/RecipeCardWithClaims'
import { trackAnalyticsEvent } from '@mindfulchefuk/utils/analytics/amplitude/amplitudeAdapter'
import {
  Recipe,
  RecipeWithQuantity,
} from '@mindfulchefuk/features/Recipes/interfaces'

type TOnboardingRecipesProps = {
  recipes: RecipeWithQuantity[]
  maximumRecipes: number
  recipesIsPending?: boolean
  onAddRecipe: (recipe: Recipe, addedFromModal?: boolean) => void
  onRemoveRecipe: (recipe: Recipe) => void
  totalNumberOfRecipes: number
}

export const OnboardingRecipes = ({
  recipes,
  maximumRecipes,
  recipesIsPending,
  onAddRecipe,
  onRemoveRecipe,
  totalNumberOfRecipes,
}: TOnboardingRecipesProps): ReactElement => {
  const { data: recipeCallouts } = useCMSPageRecipeOnboarding({
    select: (data) => data?.recipeCallouts || [],
  })

  if (recipesIsPending) {
    return (
      <Flex
        position="absolute"
        height="100%"
        width="100%"
        bg="white"
        zIndex={9}
        justifyContent="center"
      >
        <Box position="absolute" top="250px">
          <Loader />
        </Box>
      </Flex>
    )
  }

  return (
    <Grid
      templateColumns={['1fr', '1fr 1fr', '1fr 1fr 1fr']}
      listStyleType="none"
      rowGap={32}
      columnGap={24}
      pb={112}
    >
      {recipeCallouts.map((snippet, index) => {
        const snippetNumber = index + 1
        const hasEnoughRecipes = recipes.length > snippetNumber * 3 + 1

        if (!hasEnoughRecipes) return null

        return (
          <OnboardingRecipeSnippet
            id={`choose-recipes-recipe-callout-${index}`}
            key={snippet.title}
            title={snippet.title}
            description={snippet.description}
            subtext={snippet.subtext}
            row={snippetNumber * 2}
          />
        )
      })}
      {recipes.map((recipe, index) => {
        const idForModal = recipe.availabilityId.toString()

        // need to be sent to modal
        const handleIncreaseQuantity = () => onAddRecipe(recipe)
        const handleDecreaseQuantity = () => onRemoveRecipe(recipe)

        return (
          <RecipeCardWithClaims
            isBasketFull={totalNumberOfRecipes === maximumRecipes}
            onAddRecipe={handleIncreaseQuantity}
            onClick={() => {
              openModal('recipe', idForModal)
              logProductViewContentTikTok(recipe)
              trackAnalyticsEvent({
                name: 'product details clicked',
                data: {
                  'page source': 'choose recipes page',
                  'product category': ProductType.RECIPES,
                  'product type': 'recipe',
                  'product id': recipe.id,
                  'product name': recipe.title,
                },
              })
            }}
            onSubtractRecipe={handleDecreaseQuantity}
            position={index + 1}
            recipe={recipe}
            showPrice={false}
            key={recipe.title}
          />
        )
      })}
    </Grid>
  )
}
